import React, { useState, useMemo, useRef, useEffect } from 'react';
import { 
  TrendingUp, 
  Info, 
  Calendar, 
  Filter, 
  Sparkles, 
  HelpCircle, 
  CheckCircle2, 
  Clock, 
  Plus, 
  ExternalLink, 
  ChevronRight, 
  Layers, 
  Activity, 
  History as HistoryIcon,
  X,
  Droplet,
  Zap,
  ArrowRight,
  Code
} from 'lucide-react';
import { 
  TRACKED_COMPOUNDS, 
  generateSerumTimeline, 
  buildSplineSvgPath, 
  identifyCompound,
  normalizeDoseToMg
} from '../../utils/serumCalculations';

export default function SerumHub({
  protocols = [],
  auditLogs = [],
  onOpenAddModal = () => {},
  onSelectTab = () => {}
}) {
  const [timeRange, setTimeRange] = useState('3months'); // '2weeks' | '1month' | '3months' | 'all'
  const [show7DayAvg, setShow7DayAvg] = useState(false);
  const [extrapolateBackwards, setExtrapolateBackwards] = useState(true);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [hiddenCompounds, setHiddenCompounds] = useState(new Set());
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [demoStackLoaded, setDemoStackLoaded] = useState(false);
  const [isInspectOpen, setIsInspectOpen] = useState(false);

  // Fallback demo protocols if user doesn't have any GLP-1 protocols yet
  const effectiveProtocols = useMemo(() => {
    // Check if user has at least one tracked compound in active/completed protocols
    const hasAnyTracked = protocols.some(p => {
      const status = p.cycleStatus || 'Active';
      return (status === 'Active' || status === 'Completed') && Boolean(identifyCompound(p));
    });

    if (hasAnyTracked || !demoStackLoaded) {
      return protocols;
    }

    // Interactive demo stack matching the reference screenshot (Tirzepatide + Retatrutide stack)
    const today = new Date();
    const dStartTirz = new Date(today);
    dStartTirz.setDate(dStartTirz.getDate() - 56); // 8 weeks ago
    const dStartReta = new Date(today);
    dStartReta.setDate(dStartReta.getDate() - 42); // 6 weeks ago

    return [
      ...protocols,
      {
        id: 'demo-tirz',
        name: 'Tirzepatide (Zepbound)',
        category: 'Peptide',
        dosageMin: 5,
        unit: 'mg',
        administrationFormat: 'Subcutaneous Injection',
        timingOfDay: 'AM',
        frequencySchedule: 'Weekly (Tuesday AM)',
        brandOrSupplier: 'Eli Lilly',
        intendedOutcome: 'Metabolic optimization & appetite regulation',
        notes: 'Demo protocol for serum extrapolation',
        cycleStatus: 'Active',
        startDate: dStartTirz.toISOString().split('T')[0],
        endDate: null
      },
      {
        id: 'demo-reta',
        name: 'Retatrutide (Triple G)',
        category: 'Peptide',
        dosageMin: 4,
        unit: 'mg',
        administrationFormat: 'Subcutaneous Injection',
        timingOfDay: 'AM',
        frequencySchedule: 'Weekly (Saturday AM)',
        brandOrSupplier: 'Research Grade',
        intendedOutcome: 'Triple GIP/GLP-1/Glucagon agonist research stack',
        notes: 'Demo protocol for serum extrapolation',
        cycleStatus: 'Active',
        startDate: dStartReta.toISOString().split('T')[0],
        endDate: null
      }
    ];
  }, [protocols, demoStackLoaded]);

  // Compute pharmacokinetics timeline with protocol history and audit logs
  const timelineResult = useMemo(() => {
    return generateSerumTimeline({
      protocols: effectiveProtocols,
      auditLogs,
      timeRange,
      referenceDate: new Date(),
      extrapolateOngoingBackwards: extrapolateBackwards
    });
  }, [effectiveProtocols, auditLogs, timeRange, extrapolateBackwards]);

  // Expose live state to window and console for real-time inspection
  if (typeof window !== 'undefined') {
    window.__serumProtocols = effectiveProtocols;
    window.__serumAuditLogs = auditLogs;
    window.__serumTimeline = timelineResult;
  }

  useEffect(() => {
    console.log("=== [SERUM LIVE DATA DEBUG] ===");
    console.log("effectiveProtocols:", effectiveProtocols);
    console.log("auditLogs:", auditLogs);
  }, [effectiveProtocols, auditLogs]);

  const {
    hasProtocols,
    compounds = [],
    dataPoints = [],
    currentLevels = {},
    totalCurrentLevel = 0,
    steadyStateInfo = {},
    sevenDayAverages = {},
    todayPoint
  } = timelineResult;

  // Toggle compound visibility on chart
  const toggleCompoundVisibility = (id) => {
    setHiddenCompounds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Prevent hiding all compounds
        if (next.size < compounds.length - 1) {
          next.add(id);
        }
      }
      return next;
    });
  };

  // SVG Chart Dimensions & Scaling
  const svgRef = useRef(null);
  const chartWidth = 900;
  const chartHeight = 360;
  const padding = { top: 35, right: 35, bottom: 45, left: 55 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate Max Y with headroom
  const maxY = useMemo(() => {
    if (!dataPoints || dataPoints.length === 0) return 8;
    let max = 0;
    dataPoints.forEach(p => {
      compounds.forEach(c => {
        if (!hiddenCompounds.has(c.id)) {
          if ((p[c.id] || 0) > max) max = p[c.id];
        }
      });
      if (show7DayAvg) {
        Object.values(sevenDayAverages).forEach(val => {
          if (val > max) max = val;
        });
      }
    });
    // Add 20% headroom and round to clean step
    const ceil = Math.ceil(max * 1.25);
    return Math.max(4, ceil % 2 === 0 ? ceil : ceil + 1);
  }, [dataPoints, compounds, hiddenCompounds, show7DayAvg, sevenDayAverages]);

  // Scales
  const getX = (index) => {
    if (dataPoints.length <= 1) return padding.left;
    return padding.left + (index / (dataPoints.length - 1)) * innerWidth;
  };

  const getY = (value) => {
    const clamped = Math.max(0, Math.min(maxY, value || 0));
    return padding.top + innerHeight - (clamped / maxY) * innerHeight;
  };

  // Find index of Today
  const todayIndex = useMemo(() => {
    return dataPoints.findIndex(p => p.isToday);
  }, [dataPoints]);

  const todayX = todayIndex >= 0 ? getX(todayIndex) : null;

  // Generate Y-axis grid ticks (e.g. 0, 2, 4, 6, 8 mg)
  const yTicks = useMemo(() => {
    const ticks = [];
    const step = maxY <= 6 ? 1 : maxY <= 12 ? 2 : Math.ceil(maxY / 5);
    for (let val = 0; val <= maxY; val += step) {
      ticks.push(val);
    }
    return ticks;
  }, [maxY]);

  // Generate X-axis date labels (spread across ~6-8 labels)
  const xTicks = useMemo(() => {
    if (dataPoints.length === 0) return [];
    const count = Math.min(7, dataPoints.length);
    const step = Math.max(1, Math.floor((dataPoints.length - 1) / (count - 1)));
    const ticks = [];
    for (let i = 0; i < dataPoints.length; i += step) {
      ticks.push({ index: i, point: dataPoints[i] });
    }
    // Ensure the final date is shown without crowding its neighbour:
    // append it when there is enough horizontal room, otherwise swap it
    // in for the last evenly spaced tick so labels never overlap.
    const lastIndex = dataPoints.length - 1;
    const lastTick = ticks[ticks.length - 1];
    if (lastTick.index !== lastIndex) {
      // ~width of an 11px mono date label ("Nov 17") plus breathing room
      const MIN_LABEL_GAP_PX = 56;
      const pxPerPoint = lastIndex > 0 ? innerWidth / lastIndex : innerWidth;
      const gapPx = (lastIndex - lastTick.index) * pxPerPoint;
      const endTick = { index: lastIndex, point: dataPoints[lastIndex] };
      if (gapPx >= MIN_LABEL_GAP_PX) {
        ticks.push(endTick);
      } else {
        ticks[ticks.length - 1] = endTick;
      }
    }
    return ticks;
  }, [dataPoints, innerWidth]);

  // Handle Chart Mouse Move for Interactive Tooltip
  const handleMouseMove = (e) => {
    if (!svgRef.current || dataPoints.length === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    // Map mouseX from SVG coordinate space
    const relativeX = (mouseX / rect.width) * chartWidth;

    if (relativeX < padding.left || relativeX > chartWidth - padding.right) {
      setHoveredPoint(null);
      return;
    }

    const ratio = (relativeX - padding.left) / innerWidth;
    const pointIdx = Math.round(ratio * (dataPoints.length - 1));
    const clampedIdx = Math.max(0, Math.min(dataPoints.length - 1, pointIdx));
    setHoveredPoint({
      ...dataPoints[clampedIdx],
      xPos: getX(clampedIdx),
      index: clampedIdx
    });
  };

  const handleMouseLeave = () => {
    setHoveredPoint(null);
  };

  // Average steady-state saturation for display
  const overallSaturationPct = useMemo(() => {
    const infos = Object.values(steadyStateInfo);
    if (infos.length === 0) return 0;
    const sum = infos.reduce((acc, curr) => acc + (curr.saturationPct || 0), 0);
    return Math.round(sum / infos.length);
  }, [steadyStateInfo]);

  return (
    <main className="max-w-7xl mx-auto px-4 lg:px-8 py-6 space-y-6 animate-fadeIn text-slate-100">
      
      {/* Top Banner & Header Readout */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900/60 p-5 rounded-2xl border border-slate-800/80 backdrop-blur-md shadow-xl">
        <div>
          <div className="flex items-center space-x-2">
            <TrendingUp className="w-5 h-5 text-purple-400" />
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-white">
              Estimated medication level
            </h1>
          </div>
          
          <div className="mt-2 flex flex-wrap items-baseline gap-3">
            <span className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white via-slate-100 to-slate-300">
              Current level: {show7DayAvg ? (sevenDayAverages.total || totalCurrentLevel) : totalCurrentLevel} mg
            </span>
            {overallSaturationPct > 0 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                {overallSaturationPct}% steady state
              </span>
            )}
            {show7DayAvg && (
              <span className="text-xs text-amber-400 font-medium">
                (7-day rolling average)
              </span>
            )}
          </div>

          {/* Breakdown per compound pill tags */}
          {compounds.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-2 text-xs">
              <span className="text-slate-400 font-medium">Stack breakdown:</span>
              {compounds.map(c => {
                const level = show7DayAvg ? (sevenDayAverages[c.id] || currentLevels[c.id] || 0) : (currentLevels[c.id] || 0);
                const info = steadyStateInfo[c.id];
                return (
                  <div 
                    key={c.id} 
                    className="inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg bg-slate-950/80 border border-slate-800 text-slate-300 shadow-sm"
                  >
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />
                    <span className="font-semibold text-white">{c.name}:</span>
                    <span className="font-mono text-purple-300">{level} mg</span>
                    {info?.saturationPct !== undefined && (
                      <span className="text-slate-400 text-[11px]">({info.saturationPct}%)</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right Action Controls: 7-day avg toggle, Extrapolate History & Info Modal */}
        <div className="flex flex-wrap items-center gap-2 shrink-0 self-start md:self-center">
          <button
            type="button"
            onClick={() => setExtrapolateBackwards(!extrapolateBackwards)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
              extrapolateBackwards
                ? 'bg-purple-600/30 text-purple-200 border-purple-500/50 shadow-sm'
                : 'bg-slate-800/80 text-slate-400 border-slate-700 hover:text-white'
            }`}
            title="Extrapolates recurring protocol schedule backwards from history and steady-state"
          >
            <HistoryIcon className="w-3.5 h-3.5" />
            <span>Extrapolate History</span>
          </button>

          <button
            type="button"
            onClick={() => setShow7DayAvg(!show7DayAvg)}
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
              show7DayAvg
                ? 'bg-purple-600 text-white border-purple-500 shadow-md shadow-purple-600/30'
                : 'bg-slate-800/80 text-slate-300 border-slate-700 hover:bg-slate-700/80 hover:text-white'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>7-day avg</span>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsInspectOpen(prev => !prev);
              setTimeout(() => {
                const el = document.getElementById('inspect-live-data');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 60);
            }}
            aria-label="Inspect Live Data"
            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition ${
              isInspectOpen
                ? 'bg-sky-500/20 text-sky-300 border-sky-500/40'
                : 'bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-700/80 border border-slate-700'
            }`}
            title="Inspect raw protocols and audit logs currently loaded in React state"
          >
            <Code className="w-3.5 h-3.5" />
            <span>Inspect Live Data</span>
          </button>

          <button
            type="button"
            onClick={() => setIsInfoModalOpen(true)}
            aria-label="Pharmacokinetics Reference & Guide"
            className="p-2 rounded-xl bg-slate-800/80 text-slate-400 hover:text-white hover:bg-slate-700/80 border border-slate-700 transition"
            title="Pharmacokinetics Reference & Model Info"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Main Chart Container */}
      <div className="bg-[#0B0F17] border border-slate-800/90 rounded-2xl p-4 sm:p-6 shadow-2xl relative overflow-hidden">
        
        {/* Chart Top Bar: Legends & Branding */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          {/* Compound Interactive Legends */}
          <div className="flex flex-wrap items-center gap-2">
            {compounds.map(c => {
              const isHidden = hiddenCompounds.has(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCompoundVisibility(c.id)}
                  className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium transition cursor-pointer border ${
                    isHidden
                      ? 'bg-slate-900/50 border-slate-800 text-slate-500 line-through opacity-60'
                      : 'bg-slate-900 border-slate-700/80 text-slate-200 hover:border-slate-500 shadow-sm'
                  }`}
                >
                  <span 
                    className="w-2.5 h-2.5 rounded-full transition-transform" 
                    style={{ backgroundColor: isHidden ? '#64748B' : c.color }} 
                  />
                  <span className="capitalize">{c.name.toLowerCase()}</span>
                </button>
              );
            })}
          </div>

          {/* Discreet branding tag */}
          <div className="text-[11px] font-mono text-purple-400/60 uppercase tracking-widest">
            supps & peps • serum pk
          </div>
        </div>

        {/* Chart View or Empty State */}
        {!hasProtocols ? (
          <div className="py-16 text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-2xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center text-purple-400 shadow-inner">
              <Droplet className="w-7 h-7" />
            </div>
            <div className="max-w-md mx-auto">
              <h2 className="text-lg font-bold text-white">No GLP-1 or Amylin Protocols Detected</h2>
              <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">
                The serum concentration engine tracks pharmacokinetics for Semaglutide, Tirzepatide, Retatrutide, Cagrilintide, and Eloralintide using active and completed protocol cycles.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => setDemoStackLoaded(true)}
                className="inline-flex items-center space-x-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white shadow-lg shadow-purple-600/30 transition"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>Load Demo GLP-1 Stack</span>
              </button>
              <button
                type="button"
                onClick={onOpenAddModal}
                className="inline-flex items-center space-x-1.5 px-4 py-2 rounded-xl text-xs font-semibold bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 transition"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Add New Protocol</span>
              </button>
            </div>
          </div>
        ) : (
          <div className="relative">
            {/* SVG Interactive Chart */}
            <div className="w-full overflow-x-auto select-none">
              <svg
                ref={svgRef}
                viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                className="w-full h-auto min-w-[620px] max-h-[460px] cursor-crosshair"
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <defs>
                  {/* Compound Linear Gradients for Area Fills */}
                  {compounds.map(c => (
                    <linearGradient key={`grad-${c.id}`} id={`grad-${c.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={c.color} stopOpacity="0.45" />
                      <stop offset="60%" stopColor={c.color} stopOpacity="0.18" />
                      <stop offset="100%" stopColor={c.color} stopOpacity="0.01" />
                    </linearGradient>
                  ))}

                  {/* Shaded pattern for projected future area */}
                  <pattern id="futureHatch" width="10" height="10" patternTransform="rotate(45 0 0)" patternUnits="userSpaceOnUse">
                    <line x1="0" y1="0" x2="0" y2="10" stroke="#1E293B" strokeWidth="1" strokeOpacity="0.3" />
                  </pattern>
                </defs>

                {/* Horizontal Grid lines and Y-axis Ticks */}
                {yTicks.map(val => {
                  const y = getY(val);
                  return (
                    <g key={`y-${val}`}>
                      <line
                        x1={padding.left}
                        y1={y}
                        x2={chartWidth - padding.right}
                        y2={y}
                        stroke="#1E293B"
                        strokeDasharray="3 3"
                        strokeWidth="1"
                      />
                      <text
                        x={padding.left - 10}
                        y={y + 4}
                        textAnchor="end"
                        className="text-[11px] fill-slate-500 font-mono font-medium"
                      >
                        {val} mg
                      </text>
                    </g>
                  );
                })}

                {/* Vertical Grid lines for X-axis Ticks */}
                {xTicks.map(({ index, point }) => {
                  const x = getX(index);
                  return (
                    <g key={`x-grid-${index}`}>
                      <line
                        x1={x}
                        y1={padding.top}
                        x2={x}
                        y2={chartHeight - padding.bottom}
                        stroke="#1E293B"
                        strokeDasharray="2 4"
                        strokeWidth="1"
                      />
                      <text
                        x={x}
                        y={chartHeight - padding.bottom + 18}
                        textAnchor="middle"
                        className="text-[11px] fill-slate-400 font-mono"
                      >
                        {point.displayDate}
                      </text>
                    </g>
                  );
                })}

                {/* Shaded Background for Projected Future Region (right of Today) */}
                {todayX !== null && (
                  <g>
                    <rect
                      x={todayX}
                      y={padding.top}
                      width={chartWidth - padding.right - todayX}
                      height={innerHeight}
                      fill="rgba(15, 23, 42, 0.45)"
                    />
                    <rect
                      x={todayX}
                      y={padding.top}
                      width={chartWidth - padding.right - todayX}
                      height={innerHeight}
                      fill="url(#futureHatch)"
                    />
                  </g>
                )}

                {/* Area Fill Under Curves */}
                {compounds.map(c => {
                  if (hiddenCompounds.has(c.id)) return null;
                  const pathD = buildSplineSvgPath(dataPoints, (p) => getX(dataPoints.indexOf(p)), (p) => getY(p[c.id] || 0));
                  const firstX = getX(0);
                  const lastX = getX(dataPoints.length - 1);
                  const bottomY = getY(0);
                  const closedAreaD = `${pathD} L ${lastX} ${bottomY} L ${firstX} ${bottomY} Z`;

                  return (
                    <path
                      key={`area-${c.id}`}
                      d={closedAreaD}
                      fill={`url(#grad-${c.id})`}
                      className="transition-opacity duration-300"
                    />
                  );
                })}

                {/* Main Spline Curves */}
                {compounds.map(c => {
                  if (hiddenCompounds.has(c.id)) return null;
                  const pathD = buildSplineSvgPath(dataPoints, (p) => getX(dataPoints.indexOf(p)), (p) => getY(p[c.id] || 0));

                  return (
                    <path
                      key={`curve-${c.id}`}
                      d={pathD}
                      fill="none"
                      stroke={c.color}
                      strokeWidth="2.75"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="transition-all duration-300 drop-shadow-sm"
                    />
                  );
                })}

                {/* 7-Day Average Reference Horizontal Lines if enabled */}
                {show7DayAvg && compounds.map(c => {
                  if (hiddenCompounds.has(c.id)) return null;
                  const avgVal = sevenDayAverages[c.id] || 0;
                  const avgY = getY(avgVal);
                  return (
                    <g key={`avg-${c.id}`}>
                      <line
                        x1={padding.left}
                        y1={avgY}
                        x2={chartWidth - padding.right}
                        y2={avgY}
                        stroke={c.color}
                        strokeWidth="1.5"
                        strokeDasharray="5 5"
                        strokeOpacity="0.8"
                      />
                      <rect
                        x={chartWidth - padding.right - 68}
                        y={avgY - 9}
                        width="64"
                        height="18"
                        rx="4"
                        fill="#0F172A"
                        stroke={c.color}
                        strokeWidth="1"
                      />
                      <text
                        x={chartWidth - padding.right - 36}
                        y={avgY + 4}
                        textAnchor="middle"
                        className="text-[10px] font-mono font-bold"
                        fill={c.color}
                      >
                        avg {avgVal}m
                      </text>
                    </g>
                  );
                })}

                {/* Vertical "Today" Marker Line and Glowing Dots */}
                {todayX !== null && (
                  <g>
                    <line
                      x1={todayX}
                      y1={padding.top - 6}
                      x2={todayX}
                      y2={chartHeight - padding.bottom}
                      stroke="#A855F7"
                      strokeWidth="1.5"
                      strokeDasharray="4 4"
                      strokeOpacity="0.75"
                    />

                    {/* Today badge at top of marker */}
                    <rect
                      x={todayX - 22}
                      y={padding.top - 18}
                      width="44"
                      height="16"
                      rx="8"
                      fill="#581C87"
                      stroke="#C084FC"
                      strokeWidth="1"
                    />
                    <text
                      x={todayX}
                      y={padding.top - 6}
                      textAnchor="middle"
                      className="text-[9px] fill-purple-100 font-bold tracking-wide uppercase"
                    >
                      Today
                    </text>

                    {/* Glowing highlight dots on Today's level for each compound */}
                    {compounds.map(c => {
                      if (hiddenCompounds.has(c.id)) return null;
                      const level = todayPoint ? (todayPoint[c.id] || 0) : 0;
                      const yPos = getY(level);

                      return (
                        <g key={`today-dot-${c.id}`}>
                          {/* Outer pulse circle */}
                          <circle
                            cx={todayX}
                            cy={yPos}
                            r="7"
                            fill={c.color}
                            fillOpacity="0.25"
                          />
                          {/* Inner solid dot */}
                          <circle
                            cx={todayX}
                            cy={yPos}
                            r="4.5"
                            fill={c.color}
                            stroke="#0F172A"
                            strokeWidth="2"
                            className="shadow-lg"
                          />
                        </g>
                      );
                    })}
                  </g>
                )}

                {/* Interactive Crosshair & Hover Tooltip Marker */}
                {hoveredPoint && (
                  <g>
                    <line
                      x1={hoveredPoint.xPos}
                      y1={padding.top}
                      x2={hoveredPoint.xPos}
                      y2={chartHeight - padding.bottom}
                      stroke="#94A3B8"
                      strokeWidth="1"
                      strokeDasharray="2 2"
                      strokeOpacity="0.8"
                    />
                    {compounds.map(c => {
                      if (hiddenCompounds.has(c.id)) return null;
                      const val = hoveredPoint[c.id] || 0;
                      const yPos = getY(val);
                      return (
                        <circle
                          key={`hover-dot-${c.id}`}
                          cx={hoveredPoint.xPos}
                          cy={yPos}
                          r="4"
                          fill="#FFFFFF"
                          stroke={c.color}
                          strokeWidth="2"
                        />
                      );
                    })}
                  </g>
                )}
              </svg>
            </div>

            {/* Floating Glassmorphic Tooltip */}
            {hoveredPoint && (() => {
              const xPct = (hoveredPoint.xPos / chartWidth) * 100;
              const isRightSide = xPct > 55;
              const positionStyle = isRightSide
                ? { right: `${Math.max(1.5, 100 - xPct + 2)}%`, top: '12px' }
                : { left: `${Math.max(1.5, xPct + 2)}%`, top: '12px' };

              return (
                <div 
                  className="absolute z-20 pointer-events-none bg-slate-900/95 border border-slate-700/80 rounded-xl p-3 shadow-2xl backdrop-blur-md text-xs min-w-[210px] whitespace-nowrap"
                  style={positionStyle}
                >
                  <div className="flex items-center justify-between border-b border-slate-800 pb-1.5 mb-2 gap-4">
                    <span className="font-semibold text-white">
                      {hoveredPoint.dateStr}
                    </span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium shrink-0 ${
                      hoveredPoint.isToday 
                        ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                        : hoveredPoint.isFuture
                          ? 'bg-slate-800 text-slate-400'
                          : 'bg-emerald-500/10 text-emerald-400'
                    }`}>
                      {hoveredPoint.isToday ? 'Today' : hoveredPoint.isFuture ? 'Projected' : 'Recorded'}
                    </span>
                  </div>

                  <div className="space-y-1.5">
                    {compounds.map(c => {
                      if (hiddenCompounds.has(c.id)) return null;
                      const val = hoveredPoint[c.id] || 0;
                      return (
                        <div key={`tip-${c.id}`} className="flex items-center justify-between gap-4">
                          <span className="flex items-center space-x-1.5 text-slate-300">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                            <span className="capitalize">{c.name}:</span>
                          </span>
                          <span className="font-mono font-bold text-white shrink-0">{val} mg</span>
                        </div>
                      );
                    })}
                    
                    {compounds.length > 1 && (
                      <div className="pt-1.5 border-t border-slate-800 flex items-center justify-between gap-4 font-semibold">
                        <span className="text-slate-400">Total Stack:</span>
                        <span className="font-mono text-purple-300 shrink-0">{hoveredPoint.totalLevel} mg</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Bottom Timeline Sparkline Mini-Scrubber */}
            <div className="mt-4 pt-3 border-t border-slate-800/80">
              <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5 font-mono">
                <span>Timeline Range: <span className="text-slate-300 font-semibold">{timelineResult.startDateStr}</span> → <span className="text-slate-300 font-semibold">{timelineResult.endDateStr}</span></span>
                <span>Active Window: {timeRange === '6months' ? '6 MONTHS' : timeRange.toUpperCase()}</span>
              </div>
              <div className="h-6 w-full bg-slate-900/90 rounded-lg border border-slate-800 relative overflow-hidden flex items-center">
                {/* Mini background wave */}
                <svg className="w-full h-full opacity-30" preserveAspectRatio="none" viewBox="0 0 100 20">
                  <path
                    d="M 0 15 Q 15 5, 30 14 T 60 8 T 85 16 T 100 12 L 100 20 L 0 20 Z"
                    fill="#A855F7"
                  />
                </svg>

                {/* Highlighted slider thumb indicating active window */}
                <div 
                  className={`absolute top-0 bottom-0 bg-purple-500/20 border-x-2 border-purple-500/60 rounded ${
                    timeRange === '2weeks' ? 'w-1/4 right-1/4' :
                    timeRange === '1month' ? 'w-1/3 right-1/4' :
                    timeRange === '3months' ? 'w-1/2 right-4' :
                    timeRange === '6months' ? 'w-3/4 right-2' : 'w-full inset-0'
                  }`} 
                />
              </div>
            </div>

            {/* Bottom Time Range Selection Pills */}
            <div className="mt-5 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
              {[
                { id: '2weeks', label: '2 weeks' },
                { id: '1month', label: '1 month' },
                { id: '3months', label: '3 months' },
                { id: '6months', label: '6 months' },
                { id: 'all', label: 'All time' }
              ].map(opt => {
                const isActive = timeRange === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTimeRange(opt.id)}
                    className={`px-5 py-1.5 rounded-full text-xs font-semibold transition ${
                      isActive
                        ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Compound Breakdown & Pharmacokinetics Cards */}
      {hasProtocols && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base sm:text-lg font-bold text-white flex items-center space-x-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <span>Tracked Compound Breakdown</span>
            </h2>
            <span className="text-xs text-slate-400 font-mono">
              {compounds.length} Active in Stack
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {compounds.map(c => {
              const info = steadyStateInfo[c.id] || {};
              const currentLvl = currentLevels[c.id] || 0;
              const avgLvl = sevenDayAverages[c.id] || 0;

              return (
                <div 
                  key={c.id} 
                  className="bg-slate-900/70 border border-slate-800 rounded-2xl p-5 hover:border-slate-700 transition flex flex-col justify-between shadow-lg"
                >
                  <div className="space-y-3">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                          <h3 className="font-bold text-white text-base">{c.name}</h3>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">{c.mechanism}</p>
                      </div>

                      <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold border ${
                        info.cycleStatus === 'Completed'
                          ? 'bg-slate-800 text-slate-400 border-slate-700'
                          : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                      }`}>
                        {info.cycleStatus || 'Active'}
                      </span>
                    </div>

                    {/* Saturation Progress Bar */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-400">Steady State Saturation:</span>
                        <span className="font-bold text-purple-300">{info.saturationPct || 0}%</span>
                      </div>
                      <div className="w-full bg-slate-950 rounded-full h-2 border border-slate-800 overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${info.saturationPct || 0}%`,
                            backgroundColor: c.color
                          }}
                        />
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-2 text-xs pt-1">
                      <div className="bg-slate-950/70 p-2.5 rounded-xl border border-slate-800/80">
                        <span className="text-slate-400 block text-[11px]">Current Level</span>
                        <span className="font-mono font-bold text-white text-sm">{currentLvl} mg</span>
                      </div>
                      <div className="bg-slate-950/70 p-2.5 rounded-xl border border-slate-800/80">
                        <span className="text-slate-400 block text-[11px]">7-Day Average</span>
                        <span className="font-mono font-bold text-slate-200 text-sm">{avgLvl} mg</span>
                      </div>
                      <div className="bg-slate-950/70 p-2.5 rounded-xl border border-slate-800/80">
                        <span className="text-slate-400 block text-[11px]">Elimination t½</span>
                        <span className="font-mono font-bold text-slate-200 text-sm">{c.halfLifeDays} days</span>
                      </div>
                      <div className="bg-slate-950/70 p-2.5 rounded-xl border border-slate-800/80">
                        <span className="text-slate-400 block text-[11px]">Peak Tmax</span>
                        <span className="font-mono font-bold text-slate-200 text-sm">~{Math.round(c.tMaxDays * 24)} hrs</span>
                      </div>
                    </div>

                    {/* Steady State Range Details */}
                    {info.theoreticalPeak && (
                      <div className="text-[11px] text-slate-400 bg-slate-950/40 p-2 rounded-lg border border-slate-800/50 flex justify-between">
                        <span>Steady-State Range:</span>
                        <span className="font-mono text-slate-300 font-medium">
                          {info.theoreticalTrough} – {info.theoreticalPeak} mg
                        </span>
                      </div>
                    )}

                    {/* Protocol History Source Info */}
                    <div className="text-[11px] text-slate-400 bg-slate-950/40 p-2 rounded-lg border border-slate-800/50 flex items-center justify-between">
                      <span className="flex items-center space-x-1">
                        <HistoryIcon className="w-3 h-3 text-purple-400" />
                        <span>History Start:</span>
                      </span>
                      <span className="font-mono text-slate-200">
                        {info.earliestDateStr || 'Today'}
                        {info.hasAuditHistory && (
                          <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300 font-bold border border-purple-500/30">
                            Audit Synced
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Footer Protocol Link */}
                  <div className="mt-4 pt-3 border-t border-slate-800/80 flex items-center justify-between text-xs text-slate-400">
                    <span className="truncate max-w-[180px]">Protocol: {info.protocolName || c.name}</span>
                    <a
                      href="/stack"
                      onClick={(e) => {
                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) return;
                        e.preventDefault();
                        onSelectTab('stack');
                      }}
                      className="text-purple-400 hover:text-purple-300 flex items-center space-x-1 font-medium shrink-0 cursor-pointer"
                    >
                      <span>View Stack</span>
                      <ChevronRight className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Live Data Inspector */}
      <details 
        id="inspect-live-data"
        open={isInspectOpen}
        onToggle={(e) => setIsInspectOpen(e.currentTarget.open)}
        className="p-4 bg-slate-900/60 border border-slate-800 rounded-2xl text-xs font-mono text-slate-400"
      >
        <summary className="cursor-pointer font-bold text-sky-400 select-none hover:text-sky-300 flex items-center justify-between">
          <span className="flex items-center space-x-2">
            <Code className="w-4 h-4 text-sky-400" />
            <span>Inspect Live Data ({effectiveProtocols.length} Protocols, {auditLogs.length} Audit Logs)</span>
          </span>
          <span className="text-[11px] font-normal text-slate-500">
            {isInspectOpen ? 'Click to collapse' : 'Click to expand'}
          </span>
        </summary>
        <div className="mt-3 space-y-3">
          <div>
            <div className="text-white font-semibold mb-1 text-[11px] uppercase tracking-wider">Protocols in React state:</div>
            <pre className="p-3 bg-slate-950 rounded-xl overflow-x-auto text-[11px] text-slate-300 border border-slate-800/80 max-h-80 overflow-y-auto">
              {JSON.stringify(effectiveProtocols, null, 2)}
            </pre>
          </div>
          <div>
            <div className="text-white font-semibold mb-1 text-[11px] uppercase tracking-wider">Audit Logs in React state:</div>
            <pre className="p-3 bg-slate-950 rounded-xl overflow-x-auto text-[11px] text-slate-300 border border-slate-800/80 max-h-80 overflow-y-auto">
              {JSON.stringify(auditLogs, null, 2)}
            </pre>
          </div>
        </div>
      </details>

      {/* Pharmacokinetics Reference & Educational Modal */}
      {isInfoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3 mb-4">
              <div className="flex items-center space-x-2 text-white">
                <TrendingUp className="w-5 h-5 text-purple-400" />
                <h3 className="font-bold text-lg">Pharmacokinetics & Serum Modeling</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsInfoModalOpen(false)}
                className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-slate-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs sm:text-sm text-slate-300 leading-relaxed">
              <div>
                <h4 className="font-bold text-white text-sm mb-1">Two-Compartment Bateman Absorption Model</h4>
                <p className="text-slate-400 text-xs">
                  Upon subcutaneous administration, peptide concentration does not instantly spike. It undergoes a gradual absorption phase characterized by rate constant <em>k<sub>a</sub></em>, reaching maximum concentration (<em>T<sub>max</sub></em>) within 24 to 48 hours, followed by exponential elimination governed by terminal half-life (<em>k<sub>e</sub> = ln(2) / t<sub>1/2</sub></em>).
                </p>
              </div>

              <div>
                <h4 className="font-bold text-white text-sm mb-2">Tracked Compounds & Reference Constants</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border border-slate-800 rounded-lg overflow-hidden">
                    <thead className="bg-slate-950 text-slate-300">
                      <tr>
                        <th className="p-2">Compound</th>
                        <th className="p-2">Half-Life (t½)</th>
                        <th className="p-2">Peak (Tmax)</th>
                        <th className="p-2">Common Aliases</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800/80">
                      {Object.values(TRACKED_COMPOUNDS).map(comp => (
                        <tr key={comp.id} className="hover:bg-slate-800/40">
                          <td className="p-2 font-semibold text-white flex items-center space-x-1.5">
                            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: comp.color }} />
                            <span>{comp.name}</span>
                          </td>
                          <td className="p-2 font-mono text-purple-300">{comp.halfLifeDays} days</td>
                          <td className="p-2 font-mono text-slate-300">~{Math.round(comp.tMaxDays * 24)} hrs</td>
                          <td className="p-2 text-slate-400 font-mono text-[11px] truncate max-w-[200px]">
                            {comp.aliases.slice(0, 4).join(', ')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="font-bold text-white text-sm mb-1">Steady-State Accumulation (90%+ Saturation)</h4>
                <p className="text-slate-400 text-xs">
                  Because once-weekly doses occur before prior doses fully wash out, medication accumulates until the rate of elimination balances the administered dose. In clinical pharmacokinetics, steady-state is reached after approximately <strong>4 to 5 half-lives</strong> (typically 3 to 5 weeks for Tirzepatide, Retatrutide, and Semaglutide; up to 8 weeks for Eloralintide).
                </p>
              </div>

              <div className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-xl text-purple-200 text-xs">
                <strong>Disclaimer:</strong> This tool provides mathematical approximations for personal regimen planning and bio-accumulation visualization. Individual metabolism, injection site bioavailability, and receptor clearance rates may vary.
              </div>
            </div>

            <div className="mt-5 pt-3 border-t border-slate-800 flex justify-end">
              <button
                type="button"
                onClick={() => setIsInfoModalOpen(false)}
                className="px-4 py-2 rounded-xl text-xs font-semibold bg-purple-600 hover:bg-purple-500 text-white transition"
              >
                Close Reference
              </button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
}
